import React, { useMemo, useCallback, useRef, useEffect, useContext } from "react";
import { Input, Space } from "antd";
import type { TextAreaProps as AntTextAreaProps } from "antd/es/input";
import { useAndStore } from "../../base/store/useAndStore";
import { getDataSetStoreKey } from "../../base/storeKeyUtils";
import { parseChangeAction, createCommand } from "../../base/actionParser";
import { useFieldValidation } from "../../base/useFieldValidation";
import { DataSetInheritContext, useResolvedDataSet } from "../../base/DataSetInheritContext";
import { deriveScopedComponentId, mergeStoreProps } from "../../base/storePropsUtils";
import { ValidateHelp } from "../../base/ValidateHelp";
import { useBufferedStoreFieldValue } from "../../base/hooks/useBufferedStoreFieldValue";
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from "../../types/common";
import { resolveAffixRenderConfig, SPACE_ADDON_NOWRAP_STYLE } from "../../utils/inputAffixUtils";
import { prepareAntdDomProps } from "../../utils/normalizeReactStyle";

const { TextArea } = Input;

const VALIDATE_DEBOUNCE_MS = 200;

export interface AndInputAreaProps
  extends Omit<AntTextAreaProps, "onChange">,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  prefix?: string;
  suffix?: string;
  addonBefore?: string;
  addonAfter?: string;
  rules?: Array<{ type?: string; required?: boolean; message?: string; pattern?: string | RegExp; min?: number; max?: number }>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  changeAction?: string;
  pressEnterAction?: string;
  clearAction?: string;
  onChange?: (value: string, e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  index?: number;
}

export const AndInputArea: React.FC<AndInputAreaProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  changeAction,
  pressEnterAction,
  clearAction,
  onChange,
  value: propValue,
  defaultValue,
  index,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId
      ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField
      ? s.getDataSet(dataSetKey, effectiveDataSetField)
      : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  const flushToStore = useCallback(
    (newValue: string) => {
      if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, newValue);
      }
    },
    [dataSetKey, effectiveDataSetField, setDataSet],
  );

  const useStoreBinding = Boolean(dataSetKey && effectiveDataSetField && !onChange);
  const [draftValue, setDraftValue, flushDraftOnBlur] = useBufferedStoreFieldValue(
    storeValue,
    propValue,
    defaultValue,
    flushToStore,
    useStoreBinding,
  );

  const displayValue = useMemo(() => {
    if (useStoreBinding) return draftValue;
    if (storeValue !== undefined) return storeValue;
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue ?? defaultValue;
  }, [useStoreBinding, draftValue, storeValue, storeProps?.value, propValue, defaultValue]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, unknown>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const { leftAddonNode, rightAddonNode, inputRest } = useMemo(() => {
    const m = mergedProps as Record<string, unknown>;
    const {
      prefix: p,
      suffix: s,
      addonBefore: ab,
      addonAfter: aa,
      ...rest
    } = m;
    const prefixConfig = resolveAffixRenderConfig({
      iconName: p === undefined || p === null ? undefined : String(p),
      label: ab === undefined || ab === null ? undefined : String(ab),
    });
    const suffixConfig = resolveAffixRenderConfig({
      iconName: s === undefined || s === null ? undefined : String(s),
      label: aa === undefined || aa === null ? undefined : String(aa),
    });
    return {
      leftAddonNode: prefixConfig.iconNode ?? prefixConfig.labelText,
      rightAddonNode: suffixConfig.iconNode ?? suffixConfig.labelText,
      inputRest: rest,
    };
  }, [mergedProps]);

  const inputDomProps = useMemo(
    () => prepareAntdDomProps(inputRest as Record<string, unknown>),
    [inputRest],
  );

  const needsAffixCompact = useMemo(() => {
    return (
      leftAddonNode !== undefined &&
      leftAddonNode !== null &&
      leftAddonNode !== false
    ) ||
      (rightAddonNode !== undefined && rightAddonNode !== null && rightAddonNode !== false);
  }, [leftAddonNode, rightAddonNode]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      if (onChange) {
        onChange(newValue, e);
        return;
      }
      if (useStoreBinding) {
        setDraftValue(newValue);
      } else if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, newValue);
      }
      if (changeAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, newValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
      if (validateOnChange) {
        if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
        validateTimerRef.current = setTimeout(() => {
          validateTimerRef.current = null;
          validate(newValue);
        }, VALIDATE_DEBOUNCE_MS);
      }
    },
    [
      onChange,
      useStoreBinding,
      setDraftValue,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      changeAction,
      effectiveSessionId,
      id,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      validateOnChange,
      validate,
      ctx,
    ]
  );

  const handleBlur = useCallback(() => {
    if (useStoreBinding) flushDraftOnBlur();
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [useStoreBinding, flushDraftOnBlur, validateOnBlur, validate, displayValue]);

  const handlePressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const onPressEnter = (inputDomProps as { onPressEnter?: (ev: React.KeyboardEvent<HTMLTextAreaElement>) => void })
        .onPressEnter;
      if (onPressEnter) {
        onPressEnter(e);
        return;
      }
      if (pressEnterAction && effectiveSessionId && !e.shiftKey) {
        const { actionName, arg } = parseChangeAction(pressEnterAction, displayValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      inputDomProps,
      pressEnterAction,
      effectiveSessionId,
      displayValue,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      ctx,
    ]
  );

  const handleClear = useCallback(() => {
    const userOnClear = (inputDomProps as { onClear?: () => void }).onClear;
    if (userOnClear) {
      userOnClear();
      return;
    }
    if (clearAction && effectiveSessionId) {
      const { actionName, arg } = parseChangeAction(clearAction, "", {
        id,
        dataSet: effectiveDataSet,
        dataSetField: effectiveDataSetField,
        index: effectiveIndex,
        parentIndex: effectiveParentIndex,
      }, ctx);
      addCommand(effectiveSessionId, createCommand(actionName, arg));
    }
  }, [
    inputDomProps,
    clearAction,
    effectiveSessionId,
    id,
    effectiveDataSet,
    effectiveDataSetField,
    effectiveIndex,
    effectiveParentIndex,
    addCommand,
    ctx,
  ]);

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    []
  );

  const textAreaCommon = {
    ...(inputDomProps as object),
    disabled: develop ? false : (inputDomProps as { disabled?: boolean }).disabled,
    value: displayValue,
    onChange: handleChange,
    onBlur: handleBlur,
    onPressEnter: handlePressEnter,
    onClear: handleClear,
    status: help ? ("error" as const) : undefined,
    style: {
      ...((inputDomProps as { style?: React.CSSProperties }).style || {}),
      ...(needsAffixCompact ? { flex: 1, minWidth: 0 } : {}),
    },
  };

  const textAreaEl = needsAffixCompact ? (
    <Space.Compact style={{ width: "100%", display: "flex" }}>
      {leftAddonNode != null && leftAddonNode !== false ? (
        <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{leftAddonNode}</Space.Addon>
      ) : null}
      <TextArea {...textAreaCommon} />
      {rightAddonNode != null && rightAddonNode !== false ? (
        <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{rightAddonNode}</Space.Addon>
      ) : null}
    </Space.Compact>
  ) : (
    <TextArea {...textAreaCommon} />
  );

  return (
    <div style={{ width: "100%" }}>
      {textAreaEl}
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndInputArea;
